import * as React from "react";
import { Grid, TextField, InputLabel, Radio, Select } from "@mui/material";
import InputMask from "react-input-mask";
import globals from "../../../../../globals/globals";
import moment from "moment";

// helper
import CustomDatePicker from "../../../../../common/CustomDatePicker";
import {
  // Name helpers
  getNameError,
  MAX_NAME_LENGTH,

  // Address helpers
  getAddressError,
  MAX_ADDRESS_LENGTH,
  getAddressLine2Error,
  getCityError,
  MAX_CITY_LENGTH,

  // ZIP helpers
  ZIP_LENGTH,
  sanitizeZip,
  getZipError,
  ZIP_EXT_LENGTH,
  sanitizeZipExtension,
  getZipExtensionError,

  // Phone helpers
  MAX_PHONE_LENGTH,
  getPhoneNumberError,
} from "../../../../../utils/DemographicValidationHelper";
import { useFlash } from "../../../../../FlashContext";



interface Props {
  csrfToken: string;
  patient: any;
  patientInsurance: any;
  insuranceTypeOptions: any;
  unsavedChanges: boolean;
  setUnsavedChanges: any;
  onDelete: Function;
  isSecondaryInsurance: boolean;
  setPrimaryFormvalues: Function;
  setSecondaryFormValues: Function;
}

const MAX_COUNTY_LENGTH = 50;
const MAX_INSURANCE_LENGTH = 30;
const MAX_INSURANCE_PLAN_LENGTH = 100;

const normalizeOnSave = (value?: string) => {
  if (!value) return "";
  return value.trim().replace(/\s+/g, " ");
};

const formatPhoneNumber = (value: string | null | undefined) => {
  if (!value) return "";
  const cleaned = value.replace(/\D/g, "");
  const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (!match) return value;
  if (match[2]) {
    return `(${match[1]}) ${match[2]}${match[3] ? "-" + match[3] : ""}`;
  } else if (match[1]) {
    return `(${match[1]}`;
  }
  return value;
};

const InsuranceEdit: React.FC<Props> = (props) => {
  const { showToast } = useFlash();

  const normalizedZip = props.patientInsurance?.zip?.replace(/-/g, "") || "";

  // ---- form state ----
  const [insuranceType, setInsuranceType] = React.useState<string>(
    props.patientInsurance?.insurance_type || ""
  );
  const [insurancePlan, setInsurancePlan] = React.useState<string>(
    props.patientInsurance?.plan_name || ""
  ); // renamed setter
  const [insuranceNumber, setInsuranceNumber] = React.useState<string>(
    props.patientInsurance?.insured_id || ""
  );
  const [relationship, setRelationship] = React.useState<string>(
    props.patientInsurance?.relationship || ""
  );
  const [firstName, setFirstName] = React.useState<string>(
    props.patientInsurance?.first_name || ""
  );
  const [middleName, setMiddleName] = React.useState<string>(
    props.patientInsurance?.middle_name || ""
  );
  const [lastName, setLastName] = React.useState<string>(
    props.patientInsurance?.last_name || ""
  );
  const [address, setAddress] = React.useState<string>(
    props.patientInsurance?.address || ""
  );
  const [addressLine2, setAddressLine2] = React.useState<string>(
    props.patientInsurance?.address_line_2 || ""
  );
  const [city, setCity] = React.useState<string>(
    props.patientInsurance?.city || ""
  );
  const [state, setState] = React.useState<string>(
    props.patientInsurance?.state || ""
  );
  const [zip, setZip] = React.useState<string>(
    normalizedZip ? normalizedZip.slice(0, 5) : ""
  );
  const [zipExtension, setZipExtension] = React.useState<string>(
    normalizedZip && normalizedZip.length === 9 ? normalizedZip.slice(5) : ""
  );
  const [sex, setSex] = React.useState<string>(props.patientInsurance?.sex || "");
  const [county, setCounty] = React.useState<string>(
    props.patientInsurance?.county || ""
  );
  const [mobilePhoneNumber, setMobilePhoneNumber] = React.useState<string>(
    props.patientInsurance?.phone_number || ""
  );
  const [dateOfBirth, setDateOfBirth] = React.useState<any>(
    props.patientInsurance?.insured_dob
      ? moment(props.patientInsurance?.insured_dob)
      : null
  );

  // error state per-field for inline helper text
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>(
    {}
  );

  const [patientProfileError, setPatientProfileError] =
    React.useState<boolean>(false);

  // ---- helper: set field error ----
  const setFieldError = (field: string, message: string) => {
    setFieldErrors((prev) => ({ ...prev, [field]: message }));
  };
  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };

  // ---- update parent form values ----
  const updatePatientInsurance = React.useCallback(() => {
    const formValues = {
      insurance_type: insuranceType,
      plan_name: normalizeOnSave(insurancePlan),
      insured_id: normalizeOnSave(insuranceNumber),
      relationship: relationship,
      first_name: firstName,
      middle_name: middleName,
      last_name: lastName,
      address: address,
      address_line_2: addressLine2,
      city: city,
      state: state,
      zip: zip,
      zipExtension: zipExtension,
      county: county,
      sex: sex,
      phone_number: mobilePhoneNumber,
      insured_dob: dateOfBirth ? dateOfBirth.format("YYYY-MM-DD") : null,
      is_secondary: props.isSecondaryInsurance,
    };

    if (props.isSecondaryInsurance) {
      props.setSecondaryFormValues(formValues);
    } else {
      props.setPrimaryFormvalues(formValues);
    }
  }, [
    insuranceType,
    insurancePlan,
    insuranceNumber,
    relationship,
    firstName,
    middleName,
    lastName,
    address,
    addressLine2,
    city,
    state,
    zip,
    zipExtension,
    county,
    sex,
    mobilePhoneNumber,
    dateOfBirth,
    props.isSecondaryInsurance,
    props.setPrimaryFormvalues,
    props.setSecondaryFormValues,
  ]);

  // call update when relevant values change
  React.useEffect(() => {
    updatePatientInsurance();
  }, [updatePatientInsurance]);

  // ---- when relationship is self, ensure required patient profile fields exist ----
  React.useEffect(() => {
    if (relationship === "self") {
      const fields = [
        props.patient?.address_line_1,
        props.patient?.date_of_birth,
        props.patient?.city,
        props.patient?.state,
        props.patient?.zip,
        props.patient?.mobile_phone_number,
      ];
      const hasEmptyFields = fields.some(
        (field) => field === null || field === "" || typeof field === "undefined"
      );
      setPatientProfileError(hasEmptyFields);
    } else {
      setPatientProfileError(false);
    }
  }, [relationship, props.patient]);

  // ---- sync when relationship or patient changes (prefill for "self") ----
  React.useEffect(() => {
    if (relationship === "self") {
      const zipCode = props.patient?.zip ? String(props.patient.zip).slice(0, 5) : "";
      const zipCodeExtension =
        props.patient?.zip && String(props.patient.zip).length > 5
          ? String(props.patient.zip).slice(5)
          : "";

      setLastName(props?.patient.last_name || "");
      setFirstName(props?.patient.first_name || "");
      setMiddleName(props?.patient.middle_name || "");
      setDateOfBirth(
        props.patient?.date_of_birth ? moment(props.patient?.date_of_birth) : null
      );
      setAddress(props.patient?.address_line_1 || "");
      setAddressLine2(props.patient?.address_line_2 || "");
      setCity(props.patient?.city || "");
      setState(props.patient?.state || "");
      setSex(props.patient?.gender || "");
      setZip(zipCode);
      setZipExtension(zipCodeExtension);
      setCounty(props.patient?.county || "");
      setMobilePhoneNumber(props.patient?.mobile_phone_number || "");
    } else {
      const zipCode = props.patientInsurance?.zip
        ? String(props.patientInsurance.zip).slice(0, 5)
        : "";
      const zipCodeExtension =
        props.patientInsurance?.zip && String(props.patientInsurance.zip).length > 5
          ? String(props.patientInsurance.zip).slice(5)
          : "";

      setLastName(props.patientInsurance?.last_name || "");
      setFirstName(props.patientInsurance?.first_name || "");
      setMiddleName(props.patientInsurance?.middle_name || "");
      setDateOfBirth(
        props.patientInsurance?.insured_dob
          ? moment(props.patientInsurance?.insured_dob)
          : null
      );
      setAddress(props.patientInsurance?.address || "");
      setAddressLine2(props.patientInsurance?.address_line_2 || "");
      setCity(props.patientInsurance?.city || "");
      setState(props.patientInsurance?.state || "");
      setZip(zipCode);
      setSex(props.patientInsurance?.sex || "");
      setZipExtension(zipCodeExtension);
      setCounty(props.patientInsurance?.county || "");
      setMobilePhoneNumber(props.patientInsurance?.phone_number || "");
    }
  }, [
    props?.patient.first_name,
    props?.patient.last_name,
    props?.patient.middle_name,
    relationship,
    // note: we intentionally didn't include entire patient object to avoid extra re-renders
  ]);

  const handleDateChange = (date: any) => {
    setDateOfBirth(date);
    props.setUnsavedChanges(true);
  };

  // dropdown options
  const stateOptions = globals.states.map((s) => (
    <option key={s} value={s}>
      {s}
    </option>
  ));

  const countriesOptions = globals.countries.map((c) => (
    <option key={c} value={c}>
      {c}
    </option>
  ));

  const insuranceTypes = (props.insuranceTypeOptions || []).map((option: any) => (
    <option key={option?.insurance_type} value={option?.insurance_type}>
      {option?.insurance_type}
    </option>
  ));

  // ---- form-level validation used by parent to show flash/snackbar ----
  const validForm = (): boolean => {
    let errorFields: string[] = [];
    if (!insuranceType || insuranceType.length < 1) errorFields.push("Insurance Type");
    if (!insuranceNumber || insuranceNumber.length < 1) errorFields.push("Insured's ID #");
    if (!insurancePlan || insurancePlan.length < 1) errorFields.push("Plan Name or Program");
    if (!relationship || relationship.length < 1) errorFields.push("Relationship");
    if (!lastName || lastName.length < 1) errorFields.push("Insured's Last Name");
    if (!firstName || firstName.length < 1) errorFields.push("Insured's First Name");
    if (!address || address.length < 1) errorFields.push("Address");
    if (!city || city.length < 1) errorFields.push("City");
    if (!state || state.length < 1) errorFields.push("State");
    if (!county || county.length < 1) errorFields.push("County");
    if (!zip || zip.length < 1) errorFields.push("Zip");
    if (!mobilePhoneNumber || mobilePhoneNumber.length < 1) errorFields.push("PhoneNumber");

    if (errorFields.length > 0) {
      const message = `${errorFields.join(", ")} are required.`;
      showToast({ title: "Required Fields Missing", text: message, type: "error" });
      return false;
    }
    return true;
  };

  // ---- small helpers for onChange that sanitize and set unsaved flag ----
  const handleAlphanumericInput = (setter: (v: string) => void, raw: string, max = 999) => {
    const cleaned = raw.replace(/[^a-zA-Z0-9]/g, "");
    setter(cleaned.slice(0, max));
    props.setUnsavedChanges(true);
  };

  const handleTextInput = (setter: (v: string) => void, raw: string, max = 999) => {
    setter(raw.slice(0, max));
    props.setUnsavedChanges(true);
  };

  const handleNumericInput = (setter: (v: string) => void, raw: string, max = 999) => {
    const cleaned = raw.replace(/\D/g, "");
    setter(cleaned.slice(0, max));
    props.setUnsavedChanges(true);
  };

  return (
    <>
      {patientProfileError && (
        <div style={{ textAlign: "center", color: "red", fontSize: "14px" }}>
          The patient record is missing required information.
        </div>
      )}

      <Grid item xs={12} className="patient-edit-container patient-edit-form">
        {props.isSecondaryInsurance && !props.patientInsurance && (
          <div className="label-tag-container secondary-container" style={{ width: 40, marginLeft: 0 }}>
            <p className="label-tag secondary-tag">Secondary</p>
          </div>
        )}

        <Grid container>
          <Grid container className="form-container">
            {/* LEFT COLUMN */}
            <Grid item xs={2.5} sx={{ mr: 5 }}>
              {/* Insurance Type */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="insurance_type" className="field-label">Insurance Type*</InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <Select
                    id="insurance_type"
                    value={insuranceType}
                    required
                    variant="outlined"
                    className="the-field"
                    onChange={(event) => {
                      setInsuranceType(event.target.value);
                      props.setUnsavedChanges(true);
                    }}
                    native
                  >
                    <option value={""}>Select Insurance</option>
                    {insuranceTypes}
                  </Select>
                </Grid>
              </Grid>

              {/* Plan */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="plan_name" className="field-label">Insurance Plan or Program*</InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="plan_name"
                    placeholder="Enter Insurance Plan"
                    size="small"
                    value={insurancePlan}
                    className="the-field"
                    required
                    variant="outlined"
                    inputProps={{ maxLength: MAX_INSURANCE_PLAN_LENGTH, inputMode: "text" }}
                    onChange={(event) => {
                      handleTextInput(setInsurancePlan, event.target.value, MAX_INSURANCE_PLAN_LENGTH);
                      if (event.target.value?.length > 0) clearFieldError("plan_name");
                    }}
                    error={Boolean(fieldErrors["plan_name"])}
                    helperText={fieldErrors["plan_name"] || ""}
                  />
                </Grid>
              </Grid>

              {/* Insurance Number */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="insured_id" className="field-label">Insurance Number*</InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="insured_id"
                    placeholder="Enter Insurance Number"
                    size="small"
                    value={insuranceNumber}
                    className="the-field"
                    required
                    variant="outlined"
                    inputProps={{ maxLength: MAX_INSURANCE_LENGTH, inputMode: "text" }}
                    onChange={(event) => {
                      handleAlphanumericInput(setInsuranceNumber, event.target.value, MAX_INSURANCE_LENGTH);
                      if (event.target.value?.length > 0) clearFieldError("insured_id");
                    }}
                    error={Boolean(fieldErrors["insured_id"])}
                    helperText={fieldErrors["insured_id"] || ""}
                  />
                </Grid>
              </Grid>

              {/* Relationship radios */}
              <Grid item xs={12}>
                <InputLabel htmlFor="relationship" className="field-label">Patient's Relationship to Subscriber*</InputLabel>
                <Grid container item xs={12}>
                  <div className="row" style={{ height: 40 }}>
                    {["self", "spouse", "child", "other"].map((r) => (
                      <React.Fragment key={r}>
                        <Radio
                          checked={relationship === r}
                          onChange={(event) => {
                            setRelationship(event.target.value);
                            props.setUnsavedChanges(true);
                          }}
                          value={r}
                          name={`relationship-${props.isSecondaryInsurance ? "secondary" : "primary"}`}
                          inputProps={{ "aria-label": r }}
                          style={{ backgroundColor: "transparent" }}
                          sx={{ color: "#A29D9B", ml: 2, mr: 1, width: 15, height: 15 }}
                        />
                        <p className="radio-label" style={{ textTransform: "capitalize" }}>{r}</p>
                      </React.Fragment>
                    ))}
                  </div>
                </Grid>
              </Grid>
            </Grid>

            {/* MIDDLE COLUMN */}
            <Grid item xs={2.5} sx={{ mr: 5 }}>
              {/* Last Name */}
              <Grid container>
                <Grid item xs={12}><InputLabel htmlFor="last_name" className="field-label">Subscriber's Last Name*</InputLabel></Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    placeholder="Enter Last Name"
                    id="last_name"
                    size="small"
                    value={lastName}
                    disabled={relationship === "self"}
                    className="the-field"
                    required
                    variant="outlined"
                    inputProps={{ maxLength: MAX_NAME_LENGTH, inputMode: "text" }}
                    onChange={(event) => {
                      handleTextInput(setLastName, event.target.value, MAX_NAME_LENGTH);
                      if (event.target.value?.length > 0) clearFieldError("last_name");
                    }}
                    error={Boolean(getNameError(lastName, "Last Name").length > 0)}
                    helperText={getNameError(lastName, "Last Name")}
                  />
                </Grid>
              </Grid>

              {/* First Name */}
              <Grid container>
                <Grid item xs={12}><InputLabel htmlFor="first_name" className="field-label">Subscriber's First Name*</InputLabel></Grid>
                <Grid item xs={12} className="field-container">
                 <TextField
                    placeholder="Enter First Name"
                    id="first_name"
                    size="small"
                    value={relationship === "self" ? props.patient?.first_name || "" : firstName}
                    disabled={relationship === "self"}
                    className="the-field"
                    required
                    variant="outlined"
                    inputProps={{ maxLength: MAX_NAME_LENGTH, inputMode: "text" }}
                    onChange={(event) => {
                      handleTextInput(setFirstName, event.target.value, MAX_NAME_LENGTH);
                      if (event.target.value?.length > 0) clearFieldError("first_name");
                    }}
                    error={Boolean(getNameError(firstName, "First Name").length > 0)}
                    helperText={getNameError(firstName, "First Name")}
                  />
                </Grid>
              </Grid>

              {/* Middle Name */}
              <Grid container>
                <Grid item xs={12}><InputLabel htmlFor="middle_name" className="field-label">Subscriber's Middle Name</InputLabel></Grid>
                <Grid item xs={12} className="field-container">
                 <TextField
                    placeholder="Enter Middle Name"
                    id="middle_name"
                    size="small"
                    value={relationship === "self" ? props.patient?.middle_name || "" : middleName}
                    disabled={relationship === "self"}
                    className="the-field"
                    variant="outlined"
                    inputProps={{ maxLength: MAX_NAME_LENGTH, inputMode: "text" }}
                    onChange={(event) => {
                      handleTextInput(setMiddleName, event.target.value, MAX_NAME_LENGTH);
                      if (event.target.value?.length > 0) clearFieldError("middle_name");
                    }}
                    error={Boolean(getNameError(middleName, "Middle Name").length > 0)}
                    helperText={getNameError(middleName, "Middle Name")}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* RIGHT COLUMN */}
            <Grid item xs={2.5} sx={{ mr: 5 }}>
              {/* Address */}
              <Grid container>
                <Grid item xs={12}><InputLabel htmlFor="address" className="field-label">Address Line One*</InputLabel></Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="address"
                    placeholder="Enter Address Line One"
                    size="small"
                    value={address}
                    disabled={relationship === "self"}
                    required
                    variant="outlined"
                    className="the-field"
                    inputProps={{ maxLength: MAX_ADDRESS_LENGTH, inputMode: "text" }}
                    onChange={(event) => {
                      handleTextInput(setAddress, event.target.value, MAX_ADDRESS_LENGTH);
                      if (event.target.value?.length > 0) clearFieldError("address");
                    }}
                    error={getAddressError(address).error}
                    helperText={getAddressError(address).helperText}
                  />
                </Grid>
              </Grid>

              {/* Address Line 2 */}
              <Grid container>
                <Grid item xs={12}><InputLabel htmlFor="addressLine2" className="field-label">Address Line Two</InputLabel></Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="addressLine2"
                    placeholder="Enter Address Line Two"
                    size="small"
                    value={addressLine2}
                    disabled={relationship === "self"}
                    variant="outlined"
                    className="the-field"
                    inputProps={{ maxLength: MAX_ADDRESS_LENGTH, inputMode: "text" }}
                    onChange={(event) => handleTextInput(setAddressLine2, event.target.value, MAX_ADDRESS_LENGTH)}
                    error={getAddressLine2Error(addressLine2).error}
                    helperText={getAddressLine2Error(addressLine2).helperText}
                  />
                </Grid>
              </Grid>

              {/* City */}
              <Grid container>
                <Grid item xs={12}><InputLabel htmlFor="city" className="field-label">City*</InputLabel></Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="city"
                    placeholder="Enter City"
                    size="small"
                    value={city}
                    disabled={relationship === "self"}
                    required
                    variant="outlined"
                    className="the-field"
                    inputProps={{ maxLength: MAX_CITY_LENGTH }}
                    onChange={(event) => {
                      handleTextInput(setCity, event.target.value, MAX_CITY_LENGTH);
                      if (event.target.value?.length > 0) clearFieldError("city");
                    }}
                    error={getCityError(city).error}
                    helperText={getCityError(city).helperText}
                  />
                </Grid>
              </Grid>

              {/* State + County */}
              <Grid container justifyContent="space-between">
                <Grid container xs={5.5}>
                  <Grid item xs={12}><InputLabel htmlFor="state" className="field-label">State*</InputLabel></Grid>
                  <Grid item xs={12} className="field-container">
                    <Select
                      id="state"
                      value={state}
                      required
                      variant="outlined"
                      disabled={relationship === "self"}
                      className="the-field"
                      onChange={(event) => {
                        setState(event.target.value);
                        props.setUnsavedChanges(true);
                      }}
                      native
                    >
                      <option value={""}>Select</option>
                      {stateOptions}
                    </Select>
                  </Grid>
                </Grid>

                <Grid container xs={5.5} sx={{ mr: 1 }}>
                  <Grid item xs={12}><InputLabel htmlFor="county" className="field-label">County</InputLabel></Grid>
                  <Grid item xs={12} className="field-container">
                    <TextField
                      id="county"
                      placeholder="Enter County"
                      size="small"
                      value={county}
                      disabled={relationship === "self"}
                      variant="outlined"
                      className="the-field"
                      inputProps={{ maxLength: MAX_COUNTY_LENGTH, inputMode: "text" }}
                      onChange={(event) => {
                        handleTextInput(setCounty, event.target.value, MAX_COUNTY_LENGTH);
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* ZIP / Extension */}
              <Grid container justifyContent="space-between">
                <Grid container xs={5} sx={{ mr: 1 }}>
                  <Grid item xs={12}><InputLabel htmlFor="zip" className="field-label">ZIP Code*</InputLabel></Grid>
                  <Grid item xs={12} className="field-container">
                    <TextField
                      id="zip"
                      placeholder="Enter Zip"
                      size="small"
                      value={zip}
                      disabled={relationship === "self"}
                      required
                      variant="outlined"
                      className="the-field"
                      inputProps={{ inputMode: "numeric", maxLength: ZIP_LENGTH }}
                      onChange={(event) => {
                        const sanitized = sanitizeZip(event.target.value);
                        handleNumericInput(setZip, sanitized);
                        if (sanitized.length === ZIP_LENGTH) clearFieldError("zip");
                      }}
                      error={getZipError(zip).error}
                      helperText={getZipError(zip).helperText}
                      slotProps={{ htmlInput: { inputMode: "numeric", maxLength: ZIP_LENGTH } }}
                    />
                  </Grid>
                </Grid>

                <Grid item xs="auto" sx={{ display: "flex", alignItems: "center", marginLeft: "-5%" }}>
                  <span style={{ fontSize: "1.5rem", marginTop: "45px", color: "grey" }}>–</span>
                </Grid>

                <Grid container xs={5} sx={{ mr: 1, mt: 0, mb: 0, p: 0 }}>
                  <Grid item xs={12}><InputLabel htmlFor="zipExtension" className="field-label">Extension</InputLabel></Grid>
                  <Grid item xs={12} className="field-container" sx={{ mt: 0, mb: 0, p: 0 }}>
                    <TextField
                      id="zipExtension"
                      placeholder="Enter Extension"
                      size="small"
                      value={zipExtension}
                      disabled={relationship === "self"}
                      variant="outlined"
                      className="the-field"
                      inputProps={{
                        inputMode: "numeric",
                        maxLength: ZIP_EXT_LENGTH,
                      }}
                      onChange={(event) => {
                        const sanitized = sanitizeZipExtension(event.target.value);
                        handleNumericInput(setZipExtension, sanitized);
                      }}
                      error={getZipExtensionError(zipExtension).error}
                      helperText={getZipExtensionError(zipExtension).helperText}
                      slotProps={{
                        htmlInput: { inputMode: "numeric", maxLength: ZIP_EXT_LENGTH }
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* LAST COLUMN (DOB, phone, sex) */}
            <Grid item xs={2.5} sx={{ mr: 5 }}>
              <CustomDatePicker
                id={"insured_dob"}
                label={"Date Of Birth"}
                placeholder={"Select Birthdate"}
                date={dateOfBirth}
                handleDateChange={handleDateChange}
                disabled={relationship === "self"}
              />

              <Grid container>
                <Grid item xs={12}><InputLabel htmlFor="mobile_phone_number" className="field-label">Phone Number*</InputLabel></Grid>
                <Grid item xs={12} className="field-container">
                  <InputMask
                    id="mobile-phone-number"
                    mask="(999) 999-9999"
                    value={formatPhoneNumber(mobilePhoneNumber)}
                    maskChar=""
                    className="the-field"
                    size="small"
                    inputProps={{ maxLength: MAX_PHONE_LENGTH, inputMode: "numeric" }}
                    onChange={(event) => {
                      const numericValue = event.target.value.replace(/\D/g, "");
                      if (numericValue.length <= MAX_PHONE_LENGTH) {
                        setMobilePhoneNumber(numericValue);
                        props.setUnsavedChanges(true);
                        if (numericValue.length === MAX_PHONE_LENGTH) clearFieldError("mobilePhoneNumber");
                      }
                    }}
                    disabled={relationship === "self"}
                  >
                    {(inputProps) => (
                      <TextField
                        {...inputProps}
                        value={formatPhoneNumber(mobilePhoneNumber)}
                        disabled={relationship === "self"}
                        variant="outlined"
                        size="small"
                        placeholder="Enter Phone Number"
                        className="the-field"
                        error={getPhoneNumberError(mobilePhoneNumber).error}
                        helperText={getPhoneNumberError(mobilePhoneNumber).helperText}
                      />
                    )}
                  </InputMask>
                </Grid>
              </Grid>

              <Grid container className="field-container">
                <Grid item xs={12}><InputLabel htmlFor="sex" className="field-label">Sex</InputLabel></Grid>
                <Grid item xs={12}>
                  <div className="row" style={{ height: 40 }}>
                    <Radio
                      checked={sex === "Male"}
                      onChange={(event) => {
                        setSex(event.target.value);
                        props.setUnsavedChanges(true);
                      }}
                      value="Male"
                      name="sex-radio"
                      inputProps={{ "aria-label": "Male" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                    />
                    <p className="radio-label">Male</p>
                    <Radio
                      checked={sex === "Female"}
                      onChange={(event) => {
                        setSex(event.target.value);
                        props.setUnsavedChanges(true);
                      }}
                      value="Female"
                      name="sex-radio"
                      inputProps={{ "aria-label": "Female" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{ color: "#A29D9B", ml: 2, width: 32, height: 32 }}
                    />
                    <p className="radio-label">Female</p>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

InsuranceEdit.displayName = "InsuranceEdit";

export default InsuranceEdit;
